import { InvoicingDetailsDTOItem } from '@/api/trafikportalen';
import { defineMessages } from 'react-intl';
import { z } from 'zod';

export const possiblePayerDetails = [
  InvoicingDetailsDTOItem.external_customer_id,
  InvoicingDetailsDTOItem.cost_center,
  InvoicingDetailsDTOItem.account,
  InvoicingDetailsDTOItem.counterparty,
  InvoicingDetailsDTOItem.project,
  InvoicingDetailsDTOItem.purpose,
  InvoicingDetailsDTOItem.object,
  InvoicingDetailsDTOItem.article,
  InvoicingDetailsDTOItem.activity,
] as const;

export type PossibleDetails = {
  [key in (typeof possiblePayerDetails)[number]]: string | undefined;
};

export type PickRequiredFields<T, K extends keyof T> = {
  [P in K]-?: T[P];
};

export type PickOptionalFields<T, K extends keyof T> = {
  [P in K]?: T[P];
};

export type PayerDetailField = keyof PossibleDetails;

const payerDetailsLabels = defineMessages({
  external_customer_id: {
    defaultMessage: 'Kundnummer',
  },
  cost_center: {
    defaultMessage: 'Kostnadsställe',
  },
  account: {
    defaultMessage: 'Konto',
  },
  counterparty: {
    defaultMessage: 'Motpart',
  },
  project: {
    defaultMessage: 'Projekt',
  },
  purpose: {
    defaultMessage: 'Ändamål',
  },
  object: {
    defaultMessage: 'Objekt',
  },
  article: {
    defaultMessage: 'Artikel',
  },
  activity: {
    defaultMessage: 'Aktivitet',
  },
});
export const payerDetailsTranslations = {
  external_customer_id: (
    payerDetailsLabels.external_customer_id.defaultMessage as unknown as {
      type: number;
      value: string;
    }[]
  )[0].value,
  cost_center: (
    payerDetailsLabels.cost_center.defaultMessage as unknown as {
      type: number;
      value: string;
    }[]
  )[0].value,
  account: (
    payerDetailsLabels.account.defaultMessage as unknown as {
      type: number;
      value: string;
    }[]
  )[0].value,
  counterparty: (
    payerDetailsLabels.counterparty.defaultMessage as unknown as {
      type: number;
      value: string;
    }[]
  )[0].value,
  project: (
    payerDetailsLabels.project.defaultMessage as unknown as {
      type: number;
      value: string;
    }[]
  )[0].value,
  purpose: (
    payerDetailsLabels.purpose.defaultMessage as unknown as {
      type: number;
      value: string;
    }[]
  )[0].value,
  object: (
    payerDetailsLabels.object.defaultMessage as unknown as {
      type: number;
      value: string;
    }[]
  )[0].value,
  article: (
    payerDetailsLabels.article.defaultMessage as unknown as {
      type: number;
      value: string;
    }[]
  )[0].value,
  activity: (
    payerDetailsLabels.activity.defaultMessage as unknown as {
      type: number;
      value: string;
    }[]
  )[0].value,
} as const;

/**
 * Validates payer details fields to ensure:
 * - Required fields are not empty and contain only numbers
 * - Optional fields (if filled) contain only numbers
 *
 * @param params - Validation parameters
 * @param params.requiredProps - Array of required payer detail fields
 * @param params.optionalProps - Array of optional payer detail fields
 * @param params.details - The payer details object to validate
 * @param params.formatMessage - The react-intl formatMessage function
 * @param params.fieldPath - The path to the details field (e.g., 'payer_details' or 'details')
 * @param params.ctx - The Zod refinement context
 */
export const validatePayerDetails = <T extends Record<string, string>>({
  requiredProps,
  optionalProps,
  details,
  formatMessage,
  fieldPath,
  ctx,
}: {
  requiredProps: readonly string[] | undefined;
  optionalProps: readonly string[] | undefined;
  details: T;
  formatMessage: (descriptor: { defaultMessage: string }) => string;
  fieldPath: string;
  ctx: z.RefinementCtx;
}) => {
  if (!requiredProps?.length) return;

  for (const prop of requiredProps) {
    if (!details[prop] || details[prop].trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: formatMessage({ defaultMessage: 'Får inte lämnas tomt' }),
        path: [fieldPath, prop],
      });
    } else if (!/^\d+$/.test(details[prop].trim())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: formatMessage({
          defaultMessage: 'Får endast innehålla siffror',
        }),
        path: [fieldPath, prop],
      });
    }
  }

  if (optionalProps?.length) {
    for (const prop of optionalProps) {
      const value = details[prop];
      if (value && value.trim() !== '' && !/^\d+$/.test(value.trim())) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage({
            defaultMessage: 'Får endast innehålla siffror',
          }),
          path: [fieldPath, prop],
        });
      }
    }
  }
};
